import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ChunkLoadError, chunkLoadErrorShown } from "../lib/chunkLoadRecovery";
import ModuleLoadFailed from "./ModuleLoadFailed";
import type { PagePlacement } from "./pagePlacement";

type Props = {
  children: ReactNode;
  placement: PagePlacement;
};

type BoundaryProps = Props & {
  /** Смяна на адреса изчиства грешката — страницата се показва наново. */
  resetKey: string;
};

type State = { error: Error | null };

/**
 * Границата около модул, зареден при отваряне (`lazyPage.tsx`). Хваща САМО
 * провалено зареждане (`ChunkLoadError`) — то вече е минало през решението
 * за тихо презареждане (`lib/chunkLoadRecovery.ts`); всеки следващ показ
 * (модулът е отворен наново) пуска решението пак. Грешка в самата страница
 * се хвърля нагоре непроменена, както преди разделянето на кода.
 *
 * Адресът се чете тук, а не в страницата: смяната му пре-рендерира само
 * границата, а страницата (същият елемент) — не, както без разделяне.
 */
export default function LazyPageErrorBoundary(props: Props) {
  const { pathname } = useLocation();
  return <ChunkErrorBoundary {...props} resetKey={pathname} />;
}

/**
 * Нулира се при смяна на адреса, НЕ с `key`: `key` по адреса би монтирал
 * наново Справки/Склад при всяка смяна на `:page` и би изтрил състоянието им.
 */
class ChunkErrorBoundary extends Component<BoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!(error instanceof ChunkLoadError)) return;
    Sentry.captureException(error, {
      tags: { chunkLoad: "failed" },
      extra: { componentStack: info.componentStack },
    });
    chunkLoadErrorShown(error);
  }

  componentDidUpdate(previousProps: BoundaryProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!(error instanceof ChunkLoadError)) throw error;
    return <ModuleLoadFailed placement={this.props.placement} />;
  }
}
